/**
 * La puntuación (§8).
 *
 * Es una función pura y vive aquí, en el paquete compartido, por dos motivos que no son de estilo:
 *
 * - **El servidor es la única autoridad de puntuación** (principio I). Que el navegador pueda
 *   importar esta función no la convierte en autoridad: la usa para explicar, nunca para decidir.
 * - Una segunda copia de la fórmula se desincronizaría, y la divergencia aparecería como una
 *   discusión en clase sobre por qué el móvil decía 800 y el proyector 750.
 *
 * La tabla de casos de §8.2 está en `scoring.test.ts` y es la especificación, no un ejemplo.
 */
import { z } from 'zod';
/**
 * Los rangos admisibles.
 *
 * No es celo: sin ellos, un `speedWeight` mayor que uno produce **puntuaciones negativas** —la
 * fórmula multiplica por `1 − speedWeight + speedWeight × velocidad`, que con velocidad cero y peso
 * 1,5 vale −0,5— y §8.1 promete que no las hay. Como estos valores serán editables por el profesor,
 * la validación tiene que estar donde se puedan violar, no donde se escriben por defecto.
 */
export const scoringSettingsSchema = z.object({
    basePoints: z.number().int().min(1).max(10_000),
    speedWeight: z.number().min(0).max(1),
    graceMs: z.number().int().min(0).max(10_000),
    hintMultiplier: z.number().min(0).max(1),
    streakBonus: z.number().min(0).max(500),
});
export const DEFAULT_SCORING = {
    basePoints: 1000,
    speedWeight: 0.5,
    graceMs: 500,
    hintMultiplier: 0.7,
    streakBonus: 0,
};
/** Cuánto se descuenta como máximo por latencia. Impide que falsear el ida y vuelta dé ventaja. */
export const MAX_LATENCY_COMPENSATION_MS = 150;
/** Aciertos encadenados que como mucho cuentan para la bonificación. */
export const MAX_STREAK_STEPS = 5;
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
export function scoreAnswer(input) {
    const { settings } = input;
    // **El ida y vuelta ENTERO, no la mitad.** El tiempo bruto se mide de servidor a servidor —de
    // cuando emite la pregunta a cuando le llega la respuesta—, así que ya contiene los dos sentidos:
    // lo que tarda la pregunta en bajar al móvil y lo que tarda la respuesta en subir. Descontar solo
    // la mitad dejaba sin compensar el otro trayecto. Enmienda razonada de §8.1.
    const compensation = Math.min(input.rttMs, MAX_LATENCY_COMPENSATION_MS);
    const effectiveMs = Math.max(0, input.elapsedMs - compensation);
    if (!input.correct)
        return { points: 0, effectiveMs, hintApplied: false };
    const span = input.limitMs - settings.graceMs;
    // Un cuestionario con el límite por debajo del margen de gracia dividiría por cero o por un
    // número negativo. No es una configuración razonable, pero existe: se trata como «todo entra
    // dentro de la gracia» en lugar de devolver un disparate.
    const normalized = span > 0 ? clamp((effectiveMs - settings.graceMs) / span, 0, 1) : 0;
    const speed = 1 - normalized;
    const hintApplied = input.afterHint;
    const hintFactor = hintApplied ? settings.hintMultiplier : 1;
    const base = settings.basePoints *
        (1 - settings.speedWeight + settings.speedWeight * speed) *
        hintFactor *
        input.questionMultiplier;
    // La racha se suma aparte del producto, no lo multiplica: es un premio fijo por encadenar, no un
    // amplificador de todo lo demás.
    const streakSteps = clamp(input.streak - 1, 0, MAX_STREAK_STEPS);
    const streak = settings.streakBonus * streakSteps;
    // El suelo en cero es la última defensa, no la única: los rangos de `scoringSettingsSchema` ya
    // impiden llegar aquí con un producto negativo. Se deja porque «no hay puntuación negativa» es
    // una promesa del producto (§8.1, RF-017) y no debe depender de que nadie se salte la validación.
    return { points: Math.max(0, Math.round(base) + Math.round(streak)), effectiveMs, hintApplied };
}
//# sourceMappingURL=scoring.js.map